import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Tv,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Square,
  Volume2,
  VolumeX,
  ChevronDown,
  Loader2,
  CheckCircle2,
  List,
  CalendarDays,
  X,
} from 'lucide-react';
import { useIPTV } from '../../context/IPTVContext';
import { apiClient } from '../../lib/apiClient';
import { getCastPayload } from './CastModal';

/**
 * V8.8.7 — Controle remoto do Cast (OPEN TV CONNECTION).
 * Aparece em todas as telas do app enquanto uma TV transmite:
 *  - minimizado: barra "Na TV da Sala: <título>" com Controle, Play/Pausar e Parar;
 *  - aberto: controle completo (linha do tempo, anterior/próximo, volume, canais com a programação,
 *    qualidades, episódios da temporada com ✓ assistido, Parar transmissão).
 * Fala com a TV só pelo tv_sync.php (comandos e situação a cada 1,5 s).
 */

interface TvStatus {
  title?: string;
  subtitle?: string;
  streamType?: 'live' | 'movie' | 'series';
  contentId?: string;
  currentTime?: number;
  duration?: number;
  isPlaying?: boolean;
  volume?: number;
  isMuted?: boolean;
  hasPrev?: boolean;
  hasNext?: boolean;
  index?: number;
  total?: number;
  quality?: string;
  seasonNumber?: number;
  /** o navegador da TV bloqueou o som até alguém apertar OK no controle da TV */
  soundBlocked?: boolean;
}

const fmt = (s?: number) => {
  const t = Math.max(0, Math.floor(Number(s) || 0));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = t % 60;
  const mm = `${m < 10 && h ? '0' : ''}${m}:${sec < 10 ? '0' : ''}${sec}`;
  return h ? `${h}:${mm}` : mm;
};

/** Desenho de um controle remoto: corpo amarelo com contornos pretos */
const RemoteIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 40" className={className} aria-hidden="true" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="1.5" width="20" height="37" rx="7" fill="#fbbf24" stroke="#000" strokeWidth="2.2" />
    <circle cx="12" cy="12" r="5" fill="none" stroke="#000" strokeWidth="2.2" />
    <circle cx="12" cy="12" r="1.5" fill="#000" />
    <circle cx="7.8" cy="23" r="1.7" fill="#000" />
    <circle cx="16.2" cy="23" r="1.7" fill="#000" />
    <circle cx="7.8" cy="29" r="1.7" fill="#000" />
    <circle cx="16.2" cy="29" r="1.7" fill="#000" />
    <line x1="9" y1="34" x2="15" y2="34" stroke="#000" strokeWidth="2.2" />
  </svg>
);

/** Programa ao vivo agora (padrão do sistema): início, título e o que falta para terminar */
interface NowProg {
  start: number;
  end: number;
  title: string;
}
const hhmm = (ms: number) => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const leftText = (end: number) => {
  const m = Math.ceil((end - Date.now()) / 60000);
  if (m < 1) return 'terminando';
  if (m < 60) return `faltam ${m} min`;
  return `faltam ${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}min`;
};

export const CastRemote: React.FC = () => {
  const {
    isCasting,
    castSessionId,
    castDeviceName,
    setCastState,
    showToast,
    isContentWatched,
    currentChannel,
    currentMovie,
    currentSeries,
    currentEpisode,
  } = useIPTV();

  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<TvStatus | null>(null);
  const [online, setOnline] = useState(true);
  const [sending, setSending] = useState<string | null>(null);
  const [payload, setPayload] = useState<Record<string, any> | null>(null);
  const [seekPreview, setSeekPreview] = useState<number | null>(null);
  const [panel, setPanel] = useState<'list' | 'none'>('list');
  const [epg, setEpg] = useState<Record<string, NowProg | null>>({});
  const [, setTick] = useState(0);
  const seenPlaybackRef = useRef(false);
  const emptyPollsRef = useRef(0);
  const listRef = useRef<HTMLDivElement>(null);

  const active = isCasting && !!castSessionId;
  const tvName = castDeviceName || 'TV';

  // Lista mandada para a TV (canais/episódios)
  useEffect(() => {
    const load = () => setPayload(getCastPayload(castSessionId));
    load();
    window.addEventListener('opentv:cast-payload', load);
    return () => window.removeEventListener('opentv:cast-payload', load);
  }, [castSessionId]);

  // Abrir o controle a pedido (janela do Cast, depois de conectar)
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('opentv:cast-remote-open', onOpen);
    return () => window.removeEventListener('opentv:cast-remote-open', onOpen);
  }, []);

  // Nova transmissão: zera o estado
  useEffect(() => {
    seenPlaybackRef.current = false;
    emptyPollsRef.current = 0;
    setStatus(null);
    if (!active) setOpen(false);
  }, [castSessionId, active]);

  // Situação da TV a cada 1,5 s
  useEffect(() => {
    if (!active || !castSessionId) return;
    let alive = true;
    const check = async () => {
      try {
        const res = await fetch(`/api/tv_sync.php?action=get_tv_status&session_id=${encodeURIComponent(castSessionId)}`);
        const data = await res.json();
        if (!alive || !data?.success) return;
        setOnline(!!data.is_online);
        const pb: TvStatus | null = data.current_playback || null;
        if (pb) {
          seenPlaybackRef.current = true;
          emptyPollsRef.current = 0;
          setStatus(pb);
        } else if (seenPlaybackRef.current) {
          // A TV parou (Sair na TV, fim do filme, outro aparelho assumiu o perfil)
          emptyPollsRef.current += 1;
          if (emptyPollsRef.current >= 3) {
            setCastState(false);
            showToast(`Transmissão encerrada na ${tvName}.`, 'info');
          }
        }
      } catch {
        if (alive) setOnline(false);
      }
    };
    check();
    const t = setInterval(check, 1500);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [active, castSessionId, setCastState, showToast, tvName]);

  const send = useCallback(
    async (action: string, body: Record<string, any> = {}, label?: string) => {
      if (!castSessionId) return false;
      setSending(label || action);
      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        const token = apiClient.getToken();
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch('/api/tv_sync.php?action=send_command', {
          method: 'POST',
          headers,
          body: JSON.stringify({ session_id: castSessionId, command: { action, payload: body } }),
        });
        const data = await res.json().catch(() => null);
        if (!data?.success) showToast('Não foi possível enviar o comando para a TV.', 'error');
        return !!data?.success;
      } catch {
        showToast('Erro de conexão com a TV.', 'error');
        return false;
      } finally {
        setTimeout(() => setSending(null), 400);
      }
    },
    [castSessionId, showToast]
  );

  const kind: 'live' | 'movie' | 'series' =
    (status?.streamType as any) || (payload?.kind as any) || (currentChannel ? 'live' : currentSeries ? 'series' : 'movie');
  const isLive = kind === 'live';
  const isSeries = kind === 'series';
  const items: any[] = Array.isArray(payload?.items) ? payload!.items : [];
  const curIndex = typeof status?.index === 'number' ? status!.index! : Number(payload?.index) || 0;
  const curItem = items[curIndex];
  const paused = status?.isPlaying === false;
  const muted = !!status?.isMuted;
  const duration = Number(status?.duration) || 0;
  const position = seekPreview !== null ? seekPreview : Number(status?.currentTime) || 0;

  const title =
    status?.title ||
    (isLive ? curItem?.title : payload?.seriesTitle || payload?.title) ||
    currentChannel?.name ||
    currentMovie?.title ||
    currentSeries?.title ||
    'Transmissão';
  const subtitle =
    status?.subtitle ||
    (isSeries ? curItem?.title : '') ||
    (currentEpisode ? `T${currentEpisode.seasonNumber}:E${currentEpisode.episodeNumber}` : '');

  // Lista do painel: canais (todos os enviados) ou episódios da temporada atual
  const listItems = useMemo(() => {
    if (isLive) return items.map((it, i) => ({ it, i }));
    if (isSeries) {
      const season = curItem?.s;
      return items.map((it, i) => ({ it, i })).filter(({ it }) => (season ? it.s === season : true));
    }
    return [];
  }, [items, isLive, isSeries, curItem?.s]);

  // Programação "Agora" dos canais da lista (epg.php em lote, até 50)
  useEffect(() => {
    if (!open || !isLive || listItems.length === 0) return;
    const start = Math.max(0, curIndex - 20);
    const ids = listItems
      .slice(start, start + 50)
      .map(({ it }) => it.sid)
      .filter((sid: string | undefined) => sid && !(sid in epg));
    if (ids.length === 0) return;
    let alive = true;
    const dns = payload?.epgDns ? `&dns=${encodeURIComponent(payload.epgDns)}` : '';
    fetch(`/api/epg.php?action=batch&stream_ids=${encodeURIComponent(ids.join(','))}${dns}`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        const now = Date.now();
        const next: Record<string, NowProg | null> = {};
        for (const sid of ids) {
          const progs: any[] = d?.epg?.[sid] || [];
          const cur = progs.find((p) => {
            const s = new Date(p.startTime).getTime();
            const e = new Date(p.endTime).getTime();
            return now >= s && now < e;
          });
          next[sid] = cur
            ? { start: new Date(cur.startTime).getTime(), end: new Date(cur.endTime).getTime(), title: String(cur.title || '') }
            : null;
        }
        setEpg((prev) => ({ ...prev, ...next }));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [open, isLive, listItems, curIndex, payload?.epgDns, epg]);

  // "faltam X min" anda sozinho; programas que acabaram são buscados de novo
  useEffect(() => {
    if (!open || !isLive) return;
    const t = setInterval(() => {
      setTick((n) => n + 1);
      setEpg((prev) => {
        const now = Date.now();
        let changed = false;
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          const p = next[k];
          if (p && p.end <= now) {
            delete next[k];
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 30000);
    return () => clearInterval(t);
  }, [open, isLive]);

  // Abrir o controle: rola a lista até o item atual
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const el = listRef.current?.querySelector('[data-current="1"]') as HTMLElement | null;
      el?.scrollIntoView({ block: 'center' });
    }, 80);
    return () => clearTimeout(t);
  }, [open, curIndex, panel]);

  if (!active) return null;

  const stopCast = async () => {
    await send('stop', {}, 'stop');
    setCastState(false);
    showToast(`Transmissão encerrada na ${tvName}.`, 'info');
  };
  const togglePlay = () => send(paused ? 'resume' : 'pause', {}, 'play');

  const btn =
    'rounded-xl bg-zinc-800/90 hover:bg-zinc-700 active:scale-95 text-zinc-200 hover:text-white transition cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-35 disabled:cursor-not-allowed disabled:active:scale-100 focus:outline-none focus:ring-2 focus:ring-theme-accent';
  const btnPrimary =
    'rounded-xl bg-theme-accent hover:opacity-90 active:scale-95 text-black shadow-md shadow-theme-accent-sm transition cursor-pointer flex items-center justify-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-white/60';

  const hasLocalPlayback = !!(currentChannel || currentMovie || (currentSeries && currentEpisode));

  // ── Minimizado: só o desenho de um controle remoto no canto direito (toque abre o controle) ──────────
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className={`fixed right-3 z-[60] p-1 bg-transparent flex items-center justify-center active:scale-90 transition cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 rounded-xl drop-shadow-[0_6px_10px_rgba(0,0,0,0.65)] ${
          hasLocalPlayback ? 'bottom-24' : 'bottom-[84px] lg:bottom-6'
        }`}
        style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
        title={`Controle da ${tvName}${title ? ` — ${title}` : ''}`}
        aria-label="Abrir controle remoto da TV"
      >
        <RemoteIcon className="w-9 h-[60px]" />
      </button>
    );
  }

  // ── Controle completo ─────────────────────────────────────────────────────────────────────────
  const qualities: { r: string }[] = isLive && Array.isArray(curItem?.q) ? curItem.q : [];
  const curQuality = status?.quality || curItem?.cq || '';
  const seasonLabel = isSeries && curItem?.s ? `Temporada ${curItem.s}` : '';

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm select-none" onClick={() => setOpen(false)}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-zinc-950 border border-zinc-800 w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col max-h-[94dvh] overflow-hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* Cabeçalho */}
        <div className="px-4 py-3 border-b border-zinc-800/80 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-theme-accent flex items-center justify-center text-black shadow-md shadow-theme-accent-sm shrink-0">
              <Tv className="w-5 h-5 stroke-[2.5]" />
            </div>
            <div className="min-w-0">
              <p className="font-black text-sm text-white tracking-wider">OPEN TV</p>
              <p className="text-[11px] font-bold text-theme-accent-light uppercase tracking-wider truncate flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${online ? 'bg-emerald-400' : 'bg-zinc-500'}`} />
                Controle · {tvName}
              </p>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className={`${btn} h-9 px-3 text-xs font-bold`}
            title="Minimizar o controle"
          >
            <ChevronDown className="w-4 h-4" />
            Minimizar
          </button>
        </div>

        <div className="overflow-y-auto overscroll-contain p-4 space-y-4">
          {/* O que está na TV */}
          <div className="space-y-1">
            <span
              className={`inline-block text-[10px] font-black tracking-widest px-2 py-0.5 rounded-md ${
                isLive ? 'bg-red-600 text-white' : 'bg-theme-accent text-black'
              }`}
            >
              {isLive ? '● AO VIVO' : isSeries ? 'SÉRIE' : 'FILME'}
            </span>
            <h3 className="text-lg font-black text-white leading-tight break-words">{title}</h3>
            {subtitle && <p className="text-sm text-zinc-400 break-words">{subtitle}</p>}
            {!status && (
              <p className="text-xs text-zinc-500 flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" /> Aguardando a TV...
              </p>
            )}
          </div>

          {/* Linha do tempo (filme/série) */}
          {!isLive && (
            <div>
              <input
                type="range"
                min={0}
                max={Math.max(1, Math.floor(duration))}
                step={1}
                value={Math.min(Math.floor(position), Math.max(1, Math.floor(duration)))}
                disabled={!duration}
                onChange={(e) => setSeekPreview(Number(e.target.value))}
                onMouseUp={() => {
                  if (seekPreview !== null) send('seek', { time: seekPreview }, 'seek');
                  setTimeout(() => setSeekPreview(null), 1600);
                }}
                onTouchEnd={() => {
                  if (seekPreview !== null) send('seek', { time: seekPreview }, 'seek');
                  setTimeout(() => setSeekPreview(null), 1600);
                }}
                onKeyUp={() => {
                  if (seekPreview !== null) send('seek', { time: seekPreview }, 'seek');
                  setTimeout(() => setSeekPreview(null), 1600);
                }}
                className="w-full accent-[var(--theme-accent,#f59e0b)] cursor-pointer"
              />
              <div className="flex justify-between text-[11px] font-mono text-zinc-400 mt-1">
                <span>{fmt(position)}</span>
                <span>{duration ? `-${fmt(duration - position)} / ${fmt(duration)}` : '--:--'}</span>
              </div>
            </div>
          )}

          {/* Botões principais */}
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => send('prev', {}, 'prev')}
              disabled={isLive ? items.length < 2 : status?.hasPrev === false || (!isSeries && !isLive)}
              className={`${btn} h-14 flex-col text-[11px] font-bold`}
            >
              <SkipBack className="w-5 h-5" />
              {isLive ? 'Canal anterior' : 'Anterior'}
            </button>
            {isLive ? (
              <button
                onClick={() => setPanel(panel === 'list' ? 'none' : 'list')}
                disabled={items.length < 2}
                className={`${btn} h-14 flex-col text-[11px] font-bold`}
              >
                <List className="w-5 h-5" />
                Canais
              </button>
            ) : (
              <button onClick={togglePlay} className={`${btnPrimary} h-14 flex-col text-[11px] font-black`}>
                {sending === 'play' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : paused ? (
                  <Play className="w-5 h-5 fill-black" />
                ) : (
                  <Pause className="w-5 h-5 fill-black" />
                )}
                {paused ? 'Play' : 'Pausar'}
              </button>
            )}
            <button
              onClick={() => send('next', {}, 'next')}
              disabled={isLive ? items.length < 2 : status?.hasNext === false || (!isSeries && !isLive)}
              className={`${btn} h-14 flex-col text-[11px] font-bold`}
            >
              <SkipForward className="w-5 h-5" />
              {isLive ? 'Próximo canal' : 'Próximo'}
            </button>
          </div>

          {/* Qualidades (ao vivo) */}
          {qualities.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 mr-1">Qualidade</span>
              {qualities.map((q) => (
                <button
                  key={q.r}
                  onClick={() => send('quality', { r: q.r }, 'q')}
                  className={`h-9 px-3 rounded-xl text-xs font-black font-mono transition cursor-pointer ${
                    q.r === curQuality
                      ? 'bg-theme-accent text-black shadow-md shadow-theme-accent-sm'
                      : 'bg-zinc-800/90 hover:bg-zinc-700 text-zinc-200'
                  }`}
                >
                  {q.r}
                </button>
              ))}
            </div>
          )}

          {status?.soundBlocked && (
            <div className="p-3 rounded-xl bg-theme-accent-subtle border border-theme-accent-subtle text-xs text-theme-accent-light flex items-start gap-2">
              <VolumeX className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                A {tvName} está <strong>sem som</strong>: o navegador dela só libera o som depois de um toque, clique ou{' '}
                <strong>OK</strong> na própria tela (uma vez). Depois disso os próximos vídeos já começam com som.
              </span>
            </div>
          )}

          {/* Mudo (o volume fica no controle/teclado da própria TV ou aparelho) */}
          <div className={`grid gap-2 ${isSeries ? 'grid-cols-2' : 'grid-cols-1'}`}>
            <button
              onClick={() => send('toggle_mute', {}, 'mute')}
              className={`${btn} h-11 text-xs font-bold ${muted ? 'text-red-400' : ''}`}
              title={muted ? 'Ligar o som' : 'Mudo'}
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              {muted ? 'Ligar o som' : 'Mudo'}
            </button>
            {isSeries && (
              <button
                onClick={() => setPanel(panel === 'list' ? 'none' : 'list')}
                className={`${btn} h-11 text-xs font-bold ${panel === 'list' ? 'text-theme-accent-light' : ''}`}
              >
                <List className="w-4 h-4" />
                Episódios
              </button>
            )}
          </div>

          {/* Lista: canais (com o programa de agora) ou episódios da temporada */}
          {(isLive || isSeries) && listItems.length > 0 && panel === 'list' && (
            <div className="rounded-2xl bg-zinc-900/80 border border-zinc-800 overflow-hidden">
              <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 flex items-center gap-1.5">
                  {isLive ? <CalendarDays className="w-3.5 h-3.5" /> : <List className="w-3.5 h-3.5" />}
                  {isLive ? 'Canais e programação' : `Episódios${seasonLabel ? ` — ${seasonLabel}` : ''}`}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-[11px] text-zinc-500">{listItems.length}</span>
                  <button
                    onClick={() => setPanel('none')}
                    className="h-7 px-2.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white text-[11px] font-bold flex items-center gap-1 cursor-pointer"
                    title="Fechar a lista"
                  >
                    <X className="w-3.5 h-3.5" />
                    Fechar
                  </button>
                </span>
              </div>
              <div ref={listRef} className="max-h-[38dvh] overflow-y-auto overscroll-contain">
                {listItems.map(({ it, i }) => {
                  const isCur = i === curIndex;
                  const watched = !isLive && (it.w === 1 || isContentWatched(it.id));
                  const prog = isLive && it.sid ? epg[it.sid] : null;
                  const progPct = prog ? Math.min(100, Math.max(0, ((Date.now() - prog.start) / (prog.end - prog.start)) * 100)) : 0;
                  return (
                    <button
                      key={`${it.id}-${i}`}
                      data-current={isCur ? '1' : undefined}
                      onClick={() => !isCur && send('tune', { index: i }, 'tune')}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left border-b border-zinc-800/60 last:border-b-0 transition cursor-pointer ${
                        isCur ? 'bg-theme-accent-subtle' : 'hover:bg-zinc-800/70'
                      }`}
                    >
                      <span className={`w-9 text-xs font-black font-mono shrink-0 ${isCur ? 'text-theme-accent' : 'text-zinc-500'}`}>
                        {isLive ? it.num || i + 1 : `E${it.e || i + 1}`}
                      </span>
                      {isLive && it.logo ? (
                        <img
                          src={it.logo}
                          alt=""
                          className="w-8 h-8 rounded-lg object-contain bg-zinc-950 shrink-0"
                          onError={(e) => ((e.target as HTMLElement).style.visibility = 'hidden')}
                        />
                      ) : null}
                      <span className="min-w-0 flex-1">
                        <span className={`block text-sm font-bold truncate ${isCur ? 'text-theme-accent-light' : watched ? 'text-zinc-500' : 'text-white'}`}>
                          {isLive ? it.title : it.epTitle || it.title}
                        </span>
                        {isLive &&
                          (prog ? (
                            <span className="block mt-0.5">
                              <span className="block text-[10px] truncate">
                                <span className="text-zinc-400 font-mono mr-1.5">{hhmm(prog.start)}</span>
                                <span className="text-theme-accent-light font-medium">{prog.title}</span>
                                <span className="text-zinc-500"> · {leftText(prog.end)}</span>
                              </span>
                              <span className="block w-full bg-zinc-800 rounded-full h-1 overflow-hidden mt-1">
                                <span className="block bg-theme-accent h-full rounded-full" style={{ width: `${progPct}%` }} />
                              </span>
                            </span>
                          ) : (
                            <span className="block text-[10px] text-zinc-500 truncate"> </span>
                          ))}
                        {!isLive && !watched && it.p > 0 && (
                          <span className="block h-1 mt-1 rounded-full bg-zinc-800 overflow-hidden">
                            <span className="block h-full bg-theme-accent" style={{ width: `${Math.min(100, it.p)}%` }} />
                          </span>
                        )}
                      </span>
                      {isCur ? (
                        <span className="text-[10px] font-black uppercase text-theme-accent shrink-0">Na TV</span>
                      ) : watched ? (
                        <span className="text-[10px] font-black text-emerald-400 shrink-0 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Assistido
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Parar */}
          <button
            onClick={stopCast}
            className="w-full h-12 rounded-xl bg-zinc-800/90 hover:bg-red-950/60 text-red-400 hover:text-red-300 text-sm font-bold transition cursor-pointer flex items-center justify-center gap-2 active:scale-95"
          >
            <Square className="w-4 h-4" />
            Parar transmissão
          </button>
        </div>
      </div>
    </div>
  );
};
